import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { DataTable } from '../../../components/DataTable';
import { Badge } from '../../../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { AdminStocksFilters } from './AdminStocksFilters';
import { getStockBadgeClassName, getStockStatus } from '../utils';

const AdminStocksTable = ({ brandOptions, serviceDeskOptions, stocks }) => {
  const { t } = useTranslation();
  const columns = useMemo(
    () => [
      {
        id: 'image',
        header: t('Illustration'),
        enableSorting: false,
        cell: ({ row }) => (
          row.original.imageUrl
            ? (
              <img
                src={row.original.imageUrl}
                alt={row.original.label || ''}
                className="h-12 w-12 rounded border border-gray-200 object-contain"
                loading="lazy"
              />
            )
            : <span className="text-sm text-gray-400">-</span>
        ),
      },
      {
        accessorKey: 'serviceDeskName',
        header: t('Service Desk'),
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'equalsString',
        cell: ({ row }) => (
          <span className="font-medium">{row.original.serviceDeskName}</span>
        ),
      },
      {
        accessorFn: (row) => row.label || '',
        id: 'label',
        header: t('Element'),
        enableSorting: true,
        cell: ({ row }) => (
          <span className="font-medium">{row.original.label}</span>
        ),
      },
      {
        accessorFn: (row) => row.brand || '',
        id: 'brand',
        header: t('Brand'),
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'equalsString',
        cell: ({ row }) => (
          row.original.brand
            ? <Badge variant="outline" className="text-xs">{row.original.brand}</Badge>
            : <span className="text-sm text-gray-400">-</span>
        ),
      },
      {
        accessorFn: (row) => row.type || '',
        id: 'type',
        header: t('Type'),
        enableSorting: true,
        cell: ({ row }) => (
          row.original.type
            ? <Badge variant="secondary" className="text-xs">{row.original.type}</Badge>
            : <span className="text-sm text-gray-400">-</span>
        ),
      },
      {
        accessorFn: (row) => row.stock,
        id: 'stock',
        header: t('Stock'),
        enableSorting: true,
        cell: ({ row }) => (
          <Badge className={getStockBadgeClassName(row.original.stock)}>
            {row.original.stock}
          </Badge>
        ),
      },
      {
        accessorFn: (row) => getStockStatus(row.stock),
        id: 'stockStatus',
        header: t('Stock status'),
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'equalsString',
        meta: { hidden: true },
      },
    ],
    [t]
  );

  const renderCustomFilters = ({ table, columnFilters }) => (
    <AdminStocksFilters
      table={table}
      columnFilters={columnFilters}
      serviceDeskOptions={serviceDeskOptions}
      brandOptions={brandOptions}
    />
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('Stocks available')}</CardTitle>
        <CardDescription>
          {t('Sortable, filterable and exportable table of items with stock')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          data={stocks}
          columns={columns}
          emptyMessage={t('No stock available')}
          showGlobalSearch={true}
          showPagination={true}
          pageSize={50}
          customFilters={renderCustomFilters}
        />
      </CardContent>
    </Card>
  );
};

const stockShape = PropTypes.shape({
  brand: PropTypes.string,
  imageUrl: PropTypes.string,
  label: PropTypes.string.isRequired,
  objectId: PropTypes.string,
  objectKey: PropTypes.string,
  provider: PropTypes.string.isRequired,
  providerLabel: PropTypes.string.isRequired,
  serviceDeskId: PropTypes.number.isRequired,
  serviceDeskName: PropTypes.string.isRequired,
  stock: PropTypes.number.isRequired,
  type: PropTypes.string,
});

AdminStocksTable.propTypes = {
  brandOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  serviceDeskOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  stocks: PropTypes.arrayOf(stockShape).isRequired,
};

export { AdminStocksTable };
